import { create, StateCreator } from 'zustand';
import { AxiosError, isAxiosError } from 'axios';
import dayjs from 'dayjs';
import {
  activateTalents,
  addTalentCredential,
  createInternalTalent,
  deactivateTalents,
  editTalent,
  getTalents,
  removeTalentCredential,
  type GetTalentsParams,
} from '@libs/api/talents';
import {
  CredentialsType,
  StaffCreateData,
  StaffDetails,
  StaffEditData,
  StaffHasActiveShiftsError,
  StaffHasActiveShiftsErrorDetails,
  StaffListItem,
} from '@libs/models/talents/talent';
import { ErrorWithDetails } from '@libs/utils';
import { parseError } from '@libs/api/errors';
import { DEFAULT_PAGE_SIZE } from '@molecules/dataDisplay';
import { getInternalTalent } from '@libs/api/talents/single';

type TalentsListState = {
  loading: boolean;
  initiated: boolean;
  page: number;
  total: number;
  perPage: number;
  load: (params?: GetTalentsParams) => Promise<void>;
  talents: StaffListItem[];
  selectedTalents: string[];
  currentSearch: string;
  setSelectedTalents: (talents: string[]) => void;
  deactivateTalents: (ids: string[]) => ReturnType<typeof deactivateTalents>;
  activateTalents: (ids: string[]) => Promise<void>;
};

const createTalentsListSlice: StateCreator<TalentsListState, [], [], TalentsListState> = (set, get) => ({
  loading: false,
  initiated: false,
  currentSearch: '',
  currentFilters: [],
  load: async (params) => {
    set({
      loading: true,
    });
    const state = get();
    try {
      const data = await getTalents({
        page: state.page,
        size: state.perPage,
        ...params,
      });
      set({
        talents: data.items.map((t) => ({
          ...t,
          lastWorkedTime: dayjs(t.lastWorkedTime),
        })),
        total: data.totalCount,
        page: data?.pageNumber ?? 1,
        perPage: data?.pageSize ?? 10,
        initiated: true,
        currentSearch: params?.search ?? '',
        loading: false,
      });
    } finally {
      set({
        loading: false,
      });
    }
  },
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  total: 0,
  talents: [],
  selectedTalents: [],
  setSelectedTalents: (talents) => {
    set({ selectedTalents: talents });
  },
  deactivateTalents: async (ids: string[]) => {
    try {
      const result = await deactivateTalents(ids);
      return result;
    } catch (error) {
      if (isAxiosError(error)) {
        if (isStaffHasActiveShiftsError(error) && error.response) {
          throw new StaffHasActiveShiftsError(error.response.data);
        }
        if (typeof error.response?.data === 'object') {
          throw new ErrorWithDetails(error.response?.data.detail, { type: error.response?.data.status, values: {} });
        }
      }
      throw error;
    }
  },
  activateTalents: async (ids: string[]) => {
    await activateTalents(ids);
  },
});

type TalentsEditState = {
  createTalent: (data: StaffCreateData) => Promise<void>;
  saveTalent: (id: string, data: StaffEditData) => Promise<void>;
  saving: boolean;
  showEditForm: boolean;
  showCreateForm: boolean;
  openCreateForm: () => void;
  openEditForm: (id: string) => Promise<void>;
  editInitial: StaffDetails | null;
  editLoading: boolean;
  closeEditForm: () => void;
  addCredential: (userId: string, fileFile: File, type: CredentialsType, fileName: string) => Promise<void>;
  removeCredential: (userId: string, filePath: string) => Promise<void>;
  reloadCurrentTalentData: (userId: string, silent?: boolean) => Promise<void>;
};
const createTalentsEditSlice: StateCreator<TalentsListState & TalentsEditState, [], [], TalentsEditState> = (
  set,
  get,
) => ({
  saving: false,
  editInitial: null,
  editLoading: false,
  createTalent: async (data) => {
    set({ saving: true });
    try {
      await createInternalTalent(data);
    } catch (error) {
      throw parseError(error, 'talents.editForm.errors');
    } finally {
      set({ saving: false });
    }
  },
  saveTalent: async (id, data) => {
    set({ saving: true });
    try {
      await editTalent(id, data);
    } catch (error) {
      if (isAxiosError(error)) {
        throw error.response?.data;
      }
      throw error;
    } finally {
      set({ saving: false });
    }
  },
  showCreateForm: false,
  showEditForm: false,
  openCreateForm: () => {
    set({ showCreateForm: true });
  },
  closeEditForm: () => {
    set({ showEditForm: false, showCreateForm: false, editLoading: false, editInitial: null });
  },
  openEditForm: async (id) => {
    set({ showEditForm: true, editLoading: true });
    const talent = await getInternalTalent(id);
    set({
      editLoading: false,
      editInitial: talent,
    });
  },
  addCredential: async (userId: string, fileFile: File, type: CredentialsType, fileName: string) => {
    await addTalentCredential(userId, fileFile, type, fileName);
  },
  removeCredential: async (userId: string, filePath: string) => {
    await removeTalentCredential(userId, filePath);
  },
  reloadCurrentTalentData: async (userId: string, silent: boolean = false) => {
    !silent && set({ editLoading: true });
    const talent = await getInternalTalent(userId);
    set({
      editLoading: false,
      editInitial: talent,
    });
  },
});

export const useTalentsStore = create<TalentsListState & TalentsEditState>()((...a) => ({
  ...createTalentsListSlice(...a),
  ...createTalentsEditSlice(...a),
}));

function isStaffHasActiveShiftsError(error: unknown): error is AxiosError<StaffHasActiveShiftsErrorDetails> {
  return (
    isAxiosError(error) && (error.response?.data as StaffHasActiveShiftsErrorDetails)?.type === 'staffHasActiveShifts'
  );
}
